// Backend client. Hard constraint: the PWA only talks to the backend
// session proxy at /api/* — never directly to VP, AAD, or Microsoft MSGraph.
// All requests use credentials: 'include' so the session cookie travels.

/**
 * GET /api/auth/me — operational identity for an authenticated session.
 * Credential claims (vp_bearer / vp_session_id / msgraph_*) are NEVER
 * surfaced here; they live server-side on the cookie ticket only.
 * Returns 401 if no session — callers treat 401 as "send to /login".
 */
export interface AuthMeResponse {
  vpEmployeeId: string;
  vpTenantId: string;
  displayName: string;
  superAdmin: boolean;
  tenantAdmin: boolean;
}

/**
 * RFC 7807 ProblemDetails — the wire shape for every backend error
 * response. `title` carries the machine code (e.g. "vp_unauthorized"),
 * `detail` is the human message. ASP.NET adds `type` and `traceId`.
 */
export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  traceId?: string;
  rejections?: VpRejection[];
}

// One line Vantagepoint refused on save. Blank WBS levels and no dates mean VP
// did not name them (a "Phase is required" record carries the WBS1 only).
export interface VpRejection {
  wbs1: string;
  wbs2: string;
  wbs3: string;
  message: string;
  dates: string[];
}

export interface DayCell {
  index: number;
  total: number;
  regular: number;
  overtime: number;
  comment: string;
}

export interface TimesheetRow {
  wbs1: string;
  wbs1Name: string;
  wbs2: string;
  wbs2Name: string;
  wbs3: string;
  wbs3Name: string;
  // VP's Firm Name column: the project's client. Blank on time categories.
  clientName: string;
  seq: number;
  // VP's time-category band (ADR-0041): the row a category code is booked
  // onto. Empty code and false on project rows.
  category: string;
  isCategory: boolean;
  days: DayCell[];
}

export interface TimesheetResponse {
  periodStart: string;
  periodEnd: string;
  timesheetPeriodEnd: string;
  employee: string;
  employeeName: string;
  editable: string;
  // Where Vantagepoint has the period, in VP's words: 'none' (no timesheet yet),
  // 'in-progress', 'submitted' (sent for approval in VP), 'posted', 'closed'.
  state: string;
  rows: TimesheetRow[];
}

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly rejections: readonly VpRejection[];

  constructor(message: string, code: string, status: number, rejections: readonly VpRejection[] = []) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.rejections = rejections;
  }
}

async function readError(res: Response): Promise<ApiClientError> {
  let body: ProblemDetails | undefined;
  try {
    body = (await res.json()) as ProblemDetails;
  } catch {
    // not JSON — cookie-middleware 401s carry no body
  }
  const message = body?.detail ?? body?.title ?? `${res.status} ${res.statusText}`;
  const code = body?.title ?? `http_${res.status}`;
  return new ApiClientError(message, code, res.status, body?.rejections ?? []);
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...init,
  });

  // A 401 from the route guard's own identity probe is not ours to handle. The
  // guard in router.ts already redirects on that, and with the accurate message:
  // an anonymous visitor never had a session, so "please sign in" is right and
  // "your session expired" is wrong. Both firing on the same 401 was a race, and
  // whichever redirect landed first set the message (bug 6221).
  const routeGuardOwnsThis = path.startsWith('/api/auth/me');

  // A 401 anywhere means the session this identity belongs to is gone, so the
  // cached copy must not outlive it — the route guard has to be able to see the
  // expiry on the next navigation.
  if (res.status === 401) {
    identity = null;
  }

  if (res.status === 401 && !routeGuardOwnsThis) {
    // A session that died mid-use, on any other call. Route to sign-in
    // regardless of which endpoint hit the wall. Lazy-import the router to
    // avoid a module cycle (router.ts → client.ts → router.ts).
    void import('../router').then(({ default: router }) => {
      if (router.currentRoute.value.path !== '/login') {
        router.replace({ path: '/login', query: { flash: 'Your session expired. Please sign in.' } });
      }
    });
  }

  if (!res.ok) {
    throw await readError(res);
  }

  // No-content responses (e.g. POST /api/sources/{id}/disconnect → 204) carry
  // no body; calling res.json() on them throws "Unexpected end of JSON input".
  if (res.status === 204 || res.status === 205) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

/**
 * The answer is fixed for the life of a session, and three callers ask for it:
 * the navigation guard on every protected route, plus Home and Admin on mount.
 * Unshared, that was 27 round trips in one 42-minute session (#6241). Holding
 * the promise rather than the value also collapses the concurrent burst a
 * single page load produces. Signing in is a full page navigation, so a new
 * session always starts with this empty.
 */
let identity: Promise<AuthMeResponse> | null = null;

export function getAuthMe(): Promise<AuthMeResponse> {
  identity ??= request<AuthMeResponse>('/api/auth/me').catch((e: unknown) => {
    identity = null;
    throw e;
  });
  return identity;
}

/**
 * Public bootstrap config, read once at startup to configure browser telemetry.
 * Anonymous endpoint (no session needed); carries non-secret values only. The
 * connection string is empty when telemetry is unconfigured (dev / E2E).
 */
export interface ClientConfigResponse {
  appInsightsConnectionString: string;
}

export function getClientConfig(): Promise<ClientConfigResponse> {
  return request<ClientConfigResponse>('/api/client-config');
}

export function getCurrentTimesheet(periodEnd: string): Promise<TimesheetResponse> {
  return request<TimesheetResponse>(`/api/timesheet/current?periodEnd=${encodeURIComponent(periodEnd)}`);
}

/**
 * POST /api/auth/sign-out — clears the auth cookie + ticket. Idempotent;
 * a missing/unknown cookie still returns 204.
 */
export function signOut(): Promise<void> {
  // Cleared here rather than by the caller: the next sign-in may be a different
  // person, and forgetting this would hand them the previous identity.
  identity = null;
  return fetch('/api/auth/sign-out', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then(() => undefined);
}
